import customRouteFromJson from "../../domain/weather_routing/custom_route/mapper";
import WeatherRoutingRouteService from "../../services/route/service";
import createRouteDataToJson from "../../domain/weather_routing/create_route/mapper_to_json";
import HttpError from "../../services/http_error";

const localWeatherRoutingServerUrl = process.env.VUE_APP_LOCAL_WR_API_URL;
const companyId = process.env.VUE_APP_COMPANY_ID;

const routeService = new WeatherRoutingRouteService(
  localWeatherRoutingServerUrl,
  companyId,
  customRouteFromJson,
  createRouteDataToJson
);

const getDefaultState = () => {
  return {
    allRoutes: [],
    filteredRoutes: [],
    fetchLoading: true,
    fetchError: null,
    deleteLoading: false,
    deleteError: null,
    selectedRoute: null,

    currentOffset: 0,
    paginatedFetchLoading: false,
    paginatedFetchError: null,
  };
};

const state = getDefaultState();

const getters = {
  requestFailed: (state) => (state.fetchError !== null),
  deleteRequestFailed: (state) => (state.deleteError !== null),
  noRoutes: (state) => (state.filteredRoutes.length === 0 && !state.fetchLoading),
  getSelectedRouteGeoJson: (state) => {
    const routeCoordinates = state.selectedRoute !== null
      ? state.selectedRoute.routePoints.map((point) =>
        [point.longitude, point.latitude]
      )
      : [];
    const pointFeatures = routeCoordinates.map((coord, index) => {
      return {
        "id": `${index}`,
        "type": "Feature",
        "geometry": {
          "type": "Point",
          "coordinates": coord
        },
      };
    });

    return {
      type: "geojson",
      data: {
        id: "selectedRoute",
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: routeCoordinates,
            },
            properties: {
              id: "selectedRoute",
            },
          },
          ...pointFeatures
        ],
      },
    };

  },
  getSelectedRouteLineLayer: () => {
    return {
      id: "selectedRouteLine",
      type: "line",
      source: "selectedRoute",
      layout: {
        "line-cap": "butt",
        "visibility": "visible"

      },
      paint: {
        "line-color": "#01579B",
        "line-width": 2,
      },
      filter: ["==", "$type", "LineString"],
    };
  },
  getSelectedRoutePointsLayer: () => {
    return {
      id: "selectedRoutePoints",
      type: "circle",
      source: "selectedRoute",
      paint: {
        "circle-radius": 2.5,
        "circle-opacity": 0,
        "circle-stroke-width": 0.8,
        "circle-stroke-color": "#000"
      },
      filter: ["==", "$type", "Point"],
    };
  },
};

const actions = {

  async fetchAllRoutes({ commit, rootState }) {
    commit("SET_FETCH_LOADING_STATE");
    const accessToken = rootState.weatherRouting.weatherRoutingAccessToken;
    try {
      const response = await routeService.getMany(
        localStorage.getItem("quad_token"),
        accessToken,
      );
      commit("SET_FETCH_SUCCESS_STATE", response);
    } catch (e) {
      if (e instanceof HttpError) {
        // here you handle a specific case and commit the according
        // error message
        commit("SET_FETCH_FAILED_STATE");
        return;
      }
      commit("SET_FETCH_FAILED_STATE");
    }
  },

  async paginatedFetchAllRoutes({ commit, rootState }) {
    commit("SET_PAGINATED_FETCH_LOADING_STATE");
    try {
      const accessToken = rootState.weatherRouting.weatherRoutingAccessToken;
      const response = await routeService.getMany(
        localStorage.getItem("quad_token"),
        accessToken,
        state.currentOffset
      );
      commit("SET_PAGINATED_FETCH_SUCCESS_STATE", response);
    } catch (e) {
      if (e instanceof HttpError) {
        commit("SET_PAGINATED_FETCH_FAILED_STATE");
        return;
      }
      commit("SET_PAGINATED_FETCH_FAILED_STATE");
    }
  },

  async deleteRoute({ commit, dispatch, rootState }, payload) {
    commit("SET_DELETE_LOADING_STATE");
    try {
      const accessToken = rootState.weatherRouting.weatherRoutingAccessToken;
      const routeId = payload.routeId;
      const theyrId = payload.theyrId;
      await routeService.delete(
        localStorage.getItem("quad_token"),
        accessToken,
        routeId,
        theyrId
      );
      commit("SET_DELETE_SUCCESS_STATE");
      await dispatch("fetchAllRoutes");
    } catch (error) {
      if (error instanceof HttpError) {
        commit("SET_DELETE_FAILED_STATE", error.message);
        return;
      }
      commit("SET_DELETE_FAILED_STATE", "Delete failed due to unknown exception");
    }

  },

  filterRoutesByDescription({ state, commit }, payload) {
    const description = payload.description;
    if (description !== "") {
      const filteredRoutes = state.allRoutes.filter((route) => route.description.includes(description));
      commit("SET_FILTERED_ROUTES", filteredRoutes);
    }
    else {
      commit("SET_FILTERED_ROUTES", state.allRoutes);
    }
  }

};

const mutations = {
  SET_ALL_ROUTES: (state, data) => (state.allRoutes = data),
  SET_FILTERED_ROUTES: (state, data) => (state.filteredRoutes = data),
  SET_FETCH_LOADING_STATE(state) {
    state.currentOffset = 0;
    state.fetchLoading = true;
    state.fetchError = null;
  },
  SET_FETCH_FAILED_STATE(state) {
    state.fetchLoading = false;
    state.fetchError = "Could not fetch routes";
  },
  SET_FETCH_SUCCESS_STATE(state, data) {
    state.fetchLoading = false;
    state.fetchError = null;
    state.allRoutes = data;
    state.filteredRoutes = data;
  },
  SET_PAGINATED_FETCH_LOADING_STATE(state) {
    state.currentOffset += 1;
    state.paginatedFetchLoading = true;
    state.paginatedFetchError = null;
  },
  SET_PAGINATED_FETCH_FAILED_STATE(state, error) {
    state.paginatedFetchLoading = false;
    state.paginatedFetchError = error;
    state.currentOffset -= 1;
  },
  SET_PAGINATED_FETCH_SUCCESS_STATE(state, data) {
    if (data.length > 0) {
      state.paginatedFetchLoading = false;
      state.paginatedFetchError = null;
      state.allRoutes = [...state.allRoutes, ...data];
      state.filteredRoutes = [...state.filteredRoutes, ...data];
    }
    else {
      state.currentOffset -= 1;
    }
  },
  SET_DELETE_LOADING_STATE(state) {
    state.deleteLoading = true;
    state.deleteError = null;
  },
  SET_DELETE_FAILED_STATE(state, error) {
    state.deleteLoading = false;
    state.deleteError = error;
  },
  SET_DELETE_SUCCESS_STATE(state) {
    state.deleteLoading = false;
    state.deleteError = null;
  },
  SET_SELECTED_ROUTE(state, data) {
    state.selectedRoute = data;
  },
  RESET_STATE: (state) => {
    Object.assign(state, getDefaultState());
  },
};

export const weatherRoutingRoutesModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
